<template>
  <crud-modal
    :show="show"
    title="Seleccionar Billing Sequence"
    size="lg"
    :loading="loading"
    :show-save="true"
    save-text="Seleccionar y Guardar"
    save-variant="primary"
    :save-disabled="!selectedBillingSequenceId"
    cancel-text="Cancelar"
    nested
    @save="onConfirm"
    @close="$emit('close')"
  >
    <div class="billing-sequence-selector">
      <!-- Indicador de Integrador Fijado por la Fila -->
      <div
        v-if="fixedIntegradorId"
        class="mb-3 d-flex flex-wrap align-items-center justify-content-between p-2 rounded bg-light border"
      >
        <div>
          <span class="text-muted small font-weight-bold">Integrador:</span>
          <strong class="ml-1 text-primary">{{ resolvedIntegradorNombre }}</strong>
          <span class="badge badge-secondary ml-2">ID: {{ fixedIntegradorId }}</span>
        </div>
        <div v-if="nivelTitle">
          <span class="badge badge-info">{{ nivelTitle }}</span>
        </div>
      </div>

      <!-- Selector de Integrador si la fila no tiene integrador fijado -->
      <div v-else class="form-group mb-3">
        <label class="font-weight-bold small text-muted mb-1">
          Integrador <span class="text-danger">*</span>
        </label>
        <select
          class="form-control form-control-sm"
          v-model="modalIntegradorId"
          @change="onIntegradorChange"
        >
          <option :value="null">Seleccione un integrador...</option>
          <option
            v-for="item in integradoresList"
            :key="item.id_integrador || item.id"
            :value="item.id_integrador || item.id"
          >
            {{ item.integrador }}
          </option>
        </select>
        <small class="form-text text-muted">
          Seleccione el integrador para ver sus secuencias de facturación correspondientes.
        </small>
      </div>

      <!-- Barra de búsqueda y filtro rápido -->
      <div class="row mb-2" v-if="billingSequences.length > 0">
        <div class="col-md-7 col-lg-6">
          <div class="input-group input-group-sm">
            <div class="input-group-prepend">
              <span class="input-group-text"><i class="bi bi-search"></i></span>
            </div>
            <input
              type="text"
              class="form-control"
              placeholder="Buscar por ID, secuencia o descripción..."
              v-model="searchQuery"
            />
            <div class="input-group-append" v-if="searchQuery">
              <button
                class="btn btn-outline-secondary"
                type="button"
                @click="searchQuery = ''"
                title="Limpiar búsqueda"
              >
                <i class="bi bi-x"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="col-md-5 col-lg-6 text-md-right align-self-center mt-1 mt-md-0">
          <small class="text-muted">
            Mostrando {{ filteredSequences.length }} de {{ billingSequences.length }} secuencias
          </small>
        </div>
      </div>

      <!-- Spinner cargando secuencias -->
      <div v-if="loadingList" class="alert alert-light border text-center my-3 py-4">
        <div class="spinner-border spinner-border-sm text-primary mr-2" role="status"></div>
        <span class="text-muted small font-weight-bold">Cargando secuencias de facturación del integrador...</span>
      </div>

      <!-- Tabla de Secuencias -->
      <div
        v-else-if="filteredSequences.length > 0"
        class="table-responsive border rounded"
        style="max-height: 230px; overflow-y: auto;"
      >
        <table class="table table-hover table-sm mb-0">
          <thead class="thead-light sticky-top">
            <tr>
              <th scope="col" style="width: 40px;" class="text-center">#</th>
              <th scope="col" style="width: 60px;" class="text-center">ID</th>
              <th scope="col">Secuencia</th>
              <th scope="col">Descripción</th>
              <th scope="col" class="text-right">Precio c/ Imp.</th>
              <th scope="col" class="text-right">Precio s/ Imp.</th>
              <th scope="col" class="text-center">Frecuencia</th>
              <th scope="col" class="text-center">Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in filteredSequences"
              :key="item.id_billing_sequence"
              :class="{
                'table-primary font-weight-bold': String(selectedBillingSequenceId) === String(item.id_billing_sequence),
              }"
              style="cursor: pointer;"
              @click="selectItem(item)"
              @dblclick="selectAndConfirm(item)"
            >
              <td class="text-center align-middle" @click.stop="selectItem(item)">
                <input
                  type="radio"
                  name="selected_bs"
                  :value="item.id_billing_sequence"
                  :checked="String(selectedBillingSequenceId) === String(item.id_billing_sequence)"
                  @change="selectItem(item)"
                />
              </td>
              <td class="text-center align-middle font-weight-bold">
                {{ item.id_billing_sequence }}
              </td>
              <td class="align-middle">
                <code>{{ item.sequence }}</code>
                <span
                  v-if="String(currentValue) === String(item.id_billing_sequence)"
                  class="badge badge-success ml-1"
                  title="Valor actualmente configurado"
                >
                  Actual
                </span>
              </td>
              <td class="align-middle font-weight-normal">
                {{ item.descripcion || '-' }}
              </td>
              <td class="text-right align-middle">
                {{ formatMoney(item.precio_con_imp || item.full_price_tax) }}
              </td>
              <td class="text-right align-middle text-muted small font-weight-normal">
                {{ formatMoney(item.precio_sin_imp || item.full_price) }}
              </td>
              <td class="text-center align-middle small font-weight-normal">
                {{ item.frecuency ? `${item.frecuency} d` : '-' }}
              </td>
              <td class="text-center align-middle" @click.stop>
                <button
                  type="button"
                  class="btn btn-sm py-0 px-2"
                  :class="String(selectedBillingSequenceId) === String(item.id_billing_sequence) ? 'btn-primary' : 'btn-outline-primary'"
                  @click="selectAndConfirm(item)"
                >
                  Seleccionar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Estado vacío cuando no hay secuencias para el integrador -->
      <div
        v-else-if="!loadingList && targetIntegradorId && billingSequences.length === 0"
        class="alert alert-warning text-center my-3 py-3"
      >
        <i class="bi bi-exclamation-triangle mr-1"></i>
        No se encontraron secuencias de facturación configuradas para el integrador
        <strong>{{ resolvedIntegradorNombre }}</strong>.
      </div>

      <!-- Estado vacío por filtro de búsqueda -->
      <div
        v-else-if="!loadingList && billingSequences.length > 0 && filteredSequences.length === 0"
        class="alert alert-light border text-center my-3 py-3 text-muted"
      >
        <i class="bi bi-info-circle mr-1"></i>
        No se encontraron secuencias que coincidan con "{{ searchQuery }}".
      </div>

      <!-- Estado cuando falta seleccionar integrador -->
      <div
        v-else-if="!targetIntegradorId"
        class="alert alert-info text-center my-3 py-3"
      >
        <i class="bi bi-arrow-up-circle mr-1"></i>
        Seleccione un integrador para cargar las secuencias de facturación disponibles.
      </div>

      <!-- Detalle de Billing Codes correspondientes a la secuencia seleccionada -->
      <div v-if="selectedBillingSequenceId" class="mt-3">
        <div class="card border">
          <div class="card-header py-2 bg-light d-flex justify-content-between align-items-center">
            <span class="font-weight-bold small text-dark">
              <i class="bi bi-receipt mr-1 text-primary"></i>
              Billing Codes correspondientes a la secuencia seleccionada:
              <strong class="text-primary ml-1">#{{ selectedBillingSequenceId }}</strong>
              <span v-if="selectedSequenceObj && selectedSequenceObj.descripcion" class="text-muted ml-1">
                ({{ selectedSequenceObj.descripcion }})
              </span>
            </span>
            <span v-if="!loadingCodes" class="badge badge-info">
              {{ selectedBillingCodes.length }} código(s)
            </span>
          </div>
          <div class="card-body p-0">
            <!-- Spinner -->
            <div v-if="loadingCodes" class="text-center py-3">
              <div class="spinner-border spinner-border-sm text-primary mr-2" role="status"></div>
              <span class="text-muted small">Cargando billing codes de la secuencia...</span>
            </div>

            <!-- Tabla de Billing Codes -->
            <div
              v-else-if="selectedBillingCodes.length > 0"
              class="table-responsive"
              style="max-height: 200px; overflow-y: auto;"
            >
              <table class="table table-sm table-striped table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th scope="col" style="width: 60px;" class="text-center">ID</th>
                    <th scope="col">Código TAS</th>
                    <th scope="col">Descripción</th>
                    <th scope="col" class="text-right">Precio s/ Imp.</th>
                    <th scope="col" class="text-right">Precio c/ Imp.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="code in selectedBillingCodes" :key="code.id_billing_code || code.id">
                    <td class="text-center font-weight-bold align-middle">{{ code.id_billing_code || code.id }}</td>
                    <td class="align-middle"><code>{{ code.cod_tas || '-' }}</code></td>
                    <td class="align-middle">{{ code.descripcion || '-' }}</td>
                    <td class="text-right align-middle text-muted small">{{ formatMoney(code.precio_sin_imp || code.precioSinImp) }}</td>
                    <td class="text-right align-middle">{{ formatMoney(code.precio_con_imp || code.precioConImp) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Sin billing codes -->
            <div v-else class="text-center text-muted py-3 small">
              <i class="bi bi-info-circle mr-1"></i>Esta secuencia no tiene billing codes asociados.
            </div>
          </div>
        </div>
      </div>

      <!-- Error feedback -->
      <div v-if="errorMsg" class="alert alert-danger mt-2 py-1 px-2 small">
        <i class="bi bi-exclamation-circle mr-1"></i>{{ errorMsg }}
      </div>
    </div>
  </crud-modal>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import CrudModal from "../utiles/CrudModal.vue";

export default {
  name: "AtributosBillingSequenceSelectorModal",
  components: {
    CrudModal,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    atributo: {
      type: Object,
      default: null,
    },
    idIntegrador: {
      type: [Number, String],
      default: null,
    },
    integradorNombre: {
      type: String,
      default: "",
    },
    currentValue: {
      type: [Number, String],
      default: "",
    },
    nivelTitle: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modalIntegradorId: null,
      billingSequences: [],
      loadingList: false,
      searchQuery: "",
      selectedBillingSequenceId: null,
      selectedBillingCodes: [],
      loadingCodes: false,
      errorMsg: null,
    };
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    ...mapGetters("integrador", ["integradores"]),

    fixedIntegradorId() {
      if (
        this.idIntegrador !== null &&
        this.idIntegrador !== undefined &&
        this.idIntegrador !== "" &&
        this.idIntegrador !== "todos"
      ) {
        return this.idIntegrador;
      }
      return null;
    },

    targetIntegradorId() {
      return this.fixedIntegradorId || this.modalIntegradorId;
    },

    integradoresList() {
      let list = [];
      if (this.integradores && this.integradores.data && this.integradores.data.length > 0) {
        list = [...this.integradores.data];
      } else if (this.user && this.user.integradores && Array.isArray(this.user.integradores)) {
        list = [...this.user.integradores];
      }
      return list.sort((a, b) =>
        String(a.integrador || "").localeCompare(String(b.integrador || ""), undefined, { sensitivity: "base" })
      );
    },

    resolvedIntegradorNombre() {
      if (this.integradorNombre && this.fixedIntegradorId) {
        return this.integradorNombre;
      }
      const id = this.targetIntegradorId;
      if (!id) return "";
      const found = this.integradoresList.find((i) => (i.id_integrador || i.id) == id);
      return found ? found.integrador : String(id);
    },

    filteredSequences() {
      if (!this.billingSequences || !Array.isArray(this.billingSequences)) {
        return [];
      }
      if (!this.searchQuery || !this.searchQuery.trim()) {
        return this.billingSequences;
      }
      const q = this.searchQuery.trim().toLowerCase();
      return this.billingSequences.filter((bs) => {
        const id = String(bs.id_billing_sequence || "");
        const seq = String(bs.sequence || "").toLowerCase();
        const desc = String(bs.descripcion || "").toLowerCase();
        return id.includes(q) || seq.includes(q) || desc.includes(q);
      });
    },

    selectedSequenceObj() {
      if (!this.selectedBillingSequenceId || !this.billingSequences) return null;
      return (
        this.billingSequences.find(
          (bs) => String(bs.id_billing_sequence) === String(this.selectedBillingSequenceId)
        ) || null
      );
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.searchQuery = "";
        this.errorMsg = null;
        this.selectedBillingSequenceId =
          this.currentValue !== null &&
          this.currentValue !== undefined &&
          String(this.currentValue).trim() !== ""
            ? this.currentValue
            : null;

        if (this.fixedIntegradorId) {
          this.modalIntegradorId = this.fixedIntegradorId;
          this.fetchBillingSequences(this.fixedIntegradorId);
        } else if (this.modalIntegradorId) {
          this.fetchBillingSequences(this.modalIntegradorId);
        } else {
          this.billingSequences = [];
        }

        if (this.selectedBillingSequenceId) {
          this.fetchBillingCodesForSequence(this.selectedBillingSequenceId);
        } else {
          this.selectedBillingCodes = [];
        }
      }
    },
    idIntegrador(newVal) {
      if (newVal && newVal !== "todos") {
        this.modalIntegradorId = newVal;
        if (this.show) {
          this.fetchBillingSequences(newVal);
        }
      }
    },
    selectedBillingSequenceId(newId) {
      if (newId) {
        this.fetchBillingCodesForSequence(newId);
      } else {
        this.selectedBillingCodes = [];
      }
    },
  },
  methods: {
    onIntegradorChange() {
      this.selectedBillingSequenceId = null;
      this.selectedBillingCodes = [];
      if (this.modalIntegradorId) {
        this.fetchBillingSequences(this.modalIntegradorId);
      } else {
        this.billingSequences = [];
      }
    },

    fetchBillingSequences(integradorId) {
      if (!integradorId || integradorId === "todos") {
        this.billingSequences = [];
        return;
      }
      this.loadingList = true;
      this.errorMsg = null;
      axios
        .post(process.env.VUE_APP_API_URL + "billingsequence/list", {
          id_integrador: integradorId,
        })
        .then((response) => {
          const list =
            response && response.data
              ? response.data.data || response.data
              : [];
          this.billingSequences = Array.isArray(list) ? list : [];
        })
        .catch((e) => {
          this.billingSequences = [];
          this.errorMsg =
            e && e.response && e.response.data && e.response.data.error
              ? e.response.data.error
              : "Error al cargar las secuencias de facturación.";
        })
        .finally(() => {
          this.loadingList = false;
        });
    },

    fetchBillingCodesForSequence(sequenceId) {
      if (!sequenceId) {
        this.selectedBillingCodes = [];
        return;
      }
      this.loadingCodes = true;
      axios
        .post(process.env.VUE_APP_API_URL + "billingcode/getCuadro", {
          billing_codes: sequenceId,
        })
        .then((response) => {
          this.selectedBillingCodes =
            response && response.data && Array.isArray(response.data.payments)
              ? response.data.payments
              : [];
        })
        .catch(() => {
          this.selectedBillingCodes = [];
        })
        .finally(() => {
          this.loadingCodes = false;
        });
    },

    selectItem(item) {
      this.selectedBillingSequenceId = item.id_billing_sequence;
    },

    selectAndConfirm(item) {
      this.selectedBillingSequenceId = item.id_billing_sequence;
      this.onConfirm();
    },

    onConfirm() {
      if (!this.selectedBillingSequenceId) {
        return;
      }
      const selectedItem = this.billingSequences.find(
        (bs) => String(bs.id_billing_sequence) === String(this.selectedBillingSequenceId)
      );
      this.$emit("select", this.selectedBillingSequenceId, selectedItem);
    },

    formatMoney(val) {
      if (val === null || val === undefined || val === "") return "-";
      const cleaned = String(val).replace(/^\$\s*/, "").trim();
      const num = Number(cleaned);
      if (isNaN(num)) return cleaned;
      return num.toFixed(2);
    },
  },
};
</script>

<style scoped>
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1;
}
</style>
